import { KeyRound, Save } from "lucide-react"
import * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { NewPasswordFields } from "@/components/domain/new-password-fields"
import { PasswordInput } from "@/components/domain/password-input"
import { newPasswordReady, weakPasswordMessage } from "@/components/domain/password-rules"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/toast"
import { useCurrentUser, type User } from "@/features/auth"
import { ROLE_DESCRIPTION, ROLE_LABEL } from "@/lib/roles"

import {
  MAX_FIELD_LENGTH,
  profileErrorMessage,
  sessionEndsAt,
  useChangePasswordMutation,
  useProfileQuery,
  useUpdateProfileMutation,
} from "./api/profile"

/*
   Your own account: the name you appear under, and your password.

   Two independent forms rather than one Save: a rename and a password change have different
   stakes and different failures, and a single button that did both would let a wrong current
   password throw away a name edit. Neither touches role or engagement access; those are an
   administrator's, and the API has no field for them here.

   The column is capped: a form stretched across a 1600px screen is harder to read, not easier.
*/

export function ProfilePage() {
  const signedIn = useCurrentUser()
  // Refreshes the stored session too, so a name an administrator changed reaches the sidebar.
  const profile = useProfileQuery()

  // RequireAuth wraps every shell route, so a signed-in user is always present here.
  if (!signedIn) return null
  const user = profile.data ?? signedIn

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-4 py-6 sm:px-6 sm:py-8">
        <ProfileHeader user={user} />
        <Card
          id="profile-details"
          title="Your details"
          description="How you appear to the review team and on the record."
        >
          <NameForm savedName={user.name ?? ""} />
          <dl className="flex flex-col gap-3 border-t border-border-soft pt-4">
            <div className="grid gap-0.5 sm:grid-cols-[8rem_1fr] sm:gap-6">
              <dt className="text-body-sm text-text-3">Email</dt>
              <dd className="min-w-0 break-all font-id text-body-sm text-text">{user.email}</dd>
            </div>
            <div className="grid gap-0.5 sm:grid-cols-[8rem_1fr] sm:gap-6">
              <dt className="text-body-sm text-text-3">Role</dt>
              <dd className="text-body-sm text-text">
                {ROLE_LABEL[user.role]}
                <span className="text-text-3"> — {ROLE_DESCRIPTION[user.role]}</span>
              </dd>
            </div>
          </dl>
          <p className="text-caption text-text-3">
            Your email and role are set by an administrator. Ask one if either needs to change.
          </p>
        </Card>
        <Card
          id="profile-password"
          title="Password"
          description="Changing it signs you out on every device. This one stays signed in until its current session ends."
        >
          <PasswordForm />
        </Card>
      </div>
    </div>
  )
}

function ProfileHeader({ user }: { user: User }) {
  const display = user.name?.trim() || user.email
  return (
    <header className="flex items-center gap-4">
      <Avatar name={display} identity={user.email} className="size-14 text-heading" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="truncate text-subtitle text-text">{display}</h1>
          <span className="rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-caption font-medium text-text-2">
            {ROLE_LABEL[user.role]}
          </span>
        </div>
        <p className="mt-0.5 truncate font-id text-body-sm text-text-2">{user.email}</p>
      </div>
    </header>
  )
}

function Card({
  id,
  title,
  description,
  children,
}: {
  id: string
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <section
      aria-labelledby={id}
      className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-5 sm:p-6"
    >
      <div>
        <h2 id={id} className="text-heading text-text">
          {title}
        </h2>
        <p className="mt-1 text-body-sm text-text-2">{description}</p>
      </div>
      {children}
    </section>
  )
}

export function NameForm({ savedName }: { savedName: string }) {
  const update = useUpdateProfileMutation()
  const { toast } = useToast()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [name, setName] = React.useState(savedName)
  const [edited, setEdited] = React.useState(false)

  // The saved name can arrive after mount (the profile query), or change on save. Adopt it unless
  // the person is part-way through an edit.
  React.useEffect(() => {
    if (!edited) setName(savedName)
  }, [savedName, edited])

  const trimmed = name.trim()
  const tooLong = trimmed.length > MAX_FIELD_LENGTH
  const dirty = trimmed !== savedName.trim()

  return (
    <form
      noValidate
      className="flex flex-col gap-3 sm:flex-row sm:items-start"
      onSubmit={(event) => {
        event.preventDefault()
        if (!dirty || tooLong || update.isPending) return
        update.mutate(
          { name: trimmed },
          {
            onSuccess: () => {
              setEdited(false)
              toast({ tone: "success", title: "Name saved" })
            },
            onError: () => {
              inputRef.current?.focus()
            },
          },
        )
      }}
    >
      <FormField
        label="Full name"
        hint={trimmed ? undefined : "Leave it empty to be shown by your email."}
        error={
          tooLong
            ? `Keep it to ${String(MAX_FIELD_LENGTH)} characters.`
            : update.isError
              ? profileErrorMessage(update.error)
              : undefined
        }
        className="min-w-0 flex-1"
      >
        <Input
          ref={inputRef}
          value={name}
          autoComplete="name"
          onChange={(e) => {
            setName(e.target.value)
            setEdited(true)
            if (update.isError) update.reset()
          }}
        />
      </FormField>
      {/* Aligned with the input, not the label: the label row is the field's own. */}
      <Button
        type="submit"
        variant="primary"
        leadingIcon={<Save />}
        loading={update.isPending}
        loadingText="Saving"
        disabled={!dirty || tooLong}
        className="self-start sm:mt-7"
      >
        Save
      </Button>
    </form>
  )
}

export function PasswordForm() {
  const change = useChangePasswordMutation()
  const { toast } = useToast()
  const currentRef = React.useRef<HTMLInputElement>(null)
  const [current, setCurrent] = React.useState("")
  const [next, setNext] = React.useState("")
  const [confirm, setConfirm] = React.useState("")
  // Bumped on success so the fields remount: an accepted password should not stay revealed.
  const [generation, setGeneration] = React.useState(0)
  const [endsAt, setEndsAt] = React.useState<Date | null>(null)

  // The policy's rules come from password-rules; the one this screen adds is routes_profile.py's.
  const differs = next.length > 0 && next !== current
  const canSubmit = current.length > 0 && differs && newPasswordReady(next, confirm)
  const weakPassword = weakPasswordMessage(change.error)

  const edit = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    if (change.isError || change.isSuccess) change.reset()
  }

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSubmit || change.isPending) return
        change.mutate(
          { currentPassword: current, newPassword: next },
          {
            onSuccess: () => {
              // A password that has been accepted has no reason to stay in the DOM.
              setCurrent("")
              setNext("")
              setConfirm("")
              setGeneration((value) => value + 1)
              setEndsAt(sessionEndsAt())
              toast({ tone: "success", title: "Password changed" })
            },
            onError: (cause) => {
              // A weak new password is reported on its own field; the usual refusal is the
              // current password, so put the cursor back there.
              if (weakPasswordMessage(cause)) return
              currentRef.current?.focus()
              currentRef.current?.select()
            },
          },
        )
      }}
    >
      {/* Lets a password manager tie the new password to this account. */}
      <input type="hidden" autoComplete="username" value="" readOnly />

      {/* One column the width of an auth card, so the meter and checklist read as they do on
          every other screen that chooses a password. */}
      <div key={generation} className="flex max-w-md flex-col gap-4">
        <FormField label="Current password" required>
          <PasswordInput
            ref={currentRef}
            autoComplete="current-password"
            value={current}
            onChange={(event) => edit(setCurrent)(event.currentTarget.value)}
          />
        </FormField>

        <NewPasswordFields
          password={next}
          onPasswordChange={edit(setNext)}
          confirmation={confirm}
          onConfirmationChange={edit(setConfirm)}
          extraRequirements={[
            // Short enough for one line of the two-column checklist, so the rows stay aligned.
            { id: "differs", label: "Not your current password", met: differs },
          ]}
          serverError={weakPassword}
        />
      </div>

      {change.isError && !weakPassword && (
        <Alert tone="error">{profileErrorMessage(change.error)}</Alert>
      )}
      {change.isSuccess && (
        <Alert tone="success" title="Password changed">
          You are signed out on every other device.{" "}
          {endsAt
            ? `This session ends at ${endsAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}; after that, sign in with the new password.`
            : "When this session ends, sign in with the new password."}
        </Alert>
      )}

      <div>
        <Button
          type="submit"
          variant="primary"
          leadingIcon={<KeyRound />}
          loading={change.isPending}
          loadingText="Changing password"
          disabled={!canSubmit}
        >
          Change password
        </Button>
      </div>
    </form>
  )
}
